/**
 * "Preview this space" — opens this room's TV or Remote in a new tab so an operator
 * can see the space live. Each action mints a fresh, room-scoped pairing code via
 * the same admin pairing flow as code reset (never a fabricated code) and deep-links
 * the client: TV -> `/setup?llpc=<LONG_LIVED>`, Remote -> `/remote-control?key=<SHORT_LIVED>`.
 *
 * NOTE: "Open TV" mints a fresh setup code, which (like Regenerate) supersedes the
 * room's current one — surfaced in the copy so it is never a surprise.
 */

import { useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import { ArrowDiagonalOutIcon } from '@8x8/oxygen-icon';
import type { Room } from '@spaces/contracts';
import { previewLink } from '../../api';
import { useToast } from '../../toast';
import { Card, CardTitle, Muted } from '../../ui/primitives';

const Desc = styled.div`
    font-size: 13px;
    line-height: 19px;
    color: ${p => p.theme.textColor02};
    margin-bottom: 14px;
`;

const Actions = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

export function PreviewCard({ room, canPreview }: { room: Room; canPreview: boolean }): JSX.Element {
    const toast = useToast();
    const [busy, setBusy] = useState<'tv' | 'remote' | null>(null);

    const open = async (target: 'tv' | 'remote'): Promise<void> => {
        setBusy(target);
        // Open the tab synchronously-ish: mint first, then navigate the popup. (A
        // pre-opened blank tab avoids popup blockers; we set its location on success.)
        const popup = window.open('', '_blank');
        const result = await previewLink(room.id, target);

        setBusy(null);
        if (!result.ok) {
            popup?.close();
            toast.notify('error', `Couldn't open ${target === 'tv' ? 'TV' : 'Remote'}: ${result.message}`);

            return;
        }

        if (popup) {
            popup.location.href = result.data.url;
        } else {
            window.open(result.data.url, '_blank');
        }
        toast.notify('success', `${target === 'tv' ? 'TV' : 'Remote'} opened with a fresh code.`);
    };

    return (
        <Card>
            <CardTitle>Preview this space</CardTitle>
            <Desc>
                Open this room&apos;s TV or Remote in a new tab to preview it. Each opens with a
                freshly-minted, room-scoped code — opening the TV supersedes the current setup code.
            </Desc>
            {canPreview ? (
                <Actions>
                    <Button
                        variant="primary"
                        iconLeft={<ArrowDiagonalOutIcon size={15} />}
                        isDisabled={busy === 'tv'}
                        onClick={() => void open('tv')}
                    >
                        Open TV
                    </Button>
                    <Button
                        variant="secondary"
                        iconLeft={<ArrowDiagonalOutIcon size={15} />}
                        isDisabled={busy === 'remote'}
                        onClick={() => void open('remote')}
                    >
                        Open Remote
                    </Button>
                </Actions>
            ) : (
                <Muted style={{ fontSize: 12.5 }}>
                    Preview links await the pairing-service admin-scoped mint (same gate as code reset).
                </Muted>
            )}
        </Card>
    );
}
